/**
 * Drag-to-align: the pure arithmetic behind moving a cut and rippling the ones
 * that follow it.
 *
 * WHY A SEPARATE MODULE
 * ---------------------
 * Every rule that decides whether a drag is legal is a rule about the segment
 * LIST, not about pixels: how far the corridor reaches, which neighbours are
 * dragged along, where the wall is. Keeping them here means the component only
 * has to turn a finger position into a delta and ask this file what is allowed —
 * and the invariants (monotonic, no overlap, never move anything before the
 * dragged row) are checkable in one place instead of scattered through pointer
 * handlers.
 *
 * THE MODEL
 * ---------
 * A drag preserves DURATION. The user is not re-cutting the word, they are
 * saying "this cut is the right length, it is just in the wrong place". So one
 * scalar — `delta` seconds — describes the whole edit, and it is applied to the
 * dragged row plus a bounded run of the rows after it.
 *
 * THE RIPPLE, AND WHY IT STOPS
 * ----------------------------
 * A first pass that mis-places one word usually mis-places the whole connected
 * run it belongs to by the same amount, so shifting only the dragged row would
 * leave it overlapping its neighbour. But a ripple that ran to the end of the
 * task would move work a human has already judged. So it walks forward from the
 * dragged row and stops at the first of:
 *   - a real GAP (>= {@link RIPPLE_GAP_SEC} of silence between rows at their
 *     ORIGINAL positions): the next run is anchored to its own audio, and the
 *     mis-placement demonstrably did not carry across the pause;
 *   - an ANCHOR: a row a human has already placed (`placedBy === 'human'`) or
 *     already judged (`verify !== 'pending'`). Machine guesses may be swept
 *     along; human decisions may not be moved out from under the human.
 *
 * Whatever the ripple stops at then becomes the WALL: the corridor is clipped so
 * the moving run can never cross it. The clamp is returned rather than applied
 * after the fact, so the drag itself feels the wall.
 */

import type { Segment } from '../types';
import { clamp, round3 } from '../lib/num';

/** How far from its original position a cut may be dragged, in seconds. */
export const CORRIDOR_SEC = 1.0;

/** Silence at or above this at ORIGINAL positions ends the ripple. */
export const RIPPLE_GAP_SEC = 0.25;

/** A snap target this close to the dragged start captures it. */
export const SNAP_CAPTURE_SEC = 0.08;

/** Deltas smaller than this are a tap, not a move. */
export const MIN_MOVE_SEC = 0.005;

/**
 * A row whose position a human owns.
 *
 * `placedBy === 'human'` is written only by human actions (the trimmer, a
 * confirmed merge, a drag) — the model can emit `confidence: 1` on its own, so
 * confidence is NOT trusted here. `verify !== 'pending'` means someone has
 * already looked at this exact span and said something about it. Either way,
 * moving it as a side effect of a drag three rows earlier would silently
 * invalidate a judgement that was made about different audio.
 */
export function isAnchor(s: Segment): boolean {
  return s.placedBy === 'human' || s.verify !== 'pending';
}

/** Why the ripple stopped where it did — surfaced in the preview copy. */
export type RippleStop = 'end-of-task' | 'gap' | 'anchor';

export interface MovePlan {
  /** The dragged row's id. */
  segmentId: string;
  /** Index of the dragged row inside the array the plan was built from. */
  at: number;
  /** Index of the LAST row that moves. `>= at`. */
  lastAt: number;
  /** Rows that move because of the ripple, in time order (excludes the dragged one). */
  followers: Segment[];
  /** Why the ripple ended. */
  stop: RippleStop;
  /** Label of the row the ripple stopped at, when there is one. */
  stopLabel?: string;
  /** Most negative (earlier) delta allowed. `<= 0`. */
  minDelta: number;
  /** Most positive (later) delta allowed. `>= 0`. */
  maxDelta: number;
  /** Original start of the dragged row — snapping is expressed against this. */
  originStart: number;
  /** Original end of the last moving row. */
  originGroupEnd: number;
}

/**
 * Work out what a drag of `segmentId` is allowed to do.
 *
 * `segments` must be the task's rows in time order. Returns `null` when the row
 * is not in the list (it was removed under the lane).
 */
export function planMove(
  segments: Segment[],
  segmentId: string,
  fileDurationSec: number,
): MovePlan | null {
  const at = segments.findIndex((s) => s.id === segmentId);
  if (at < 0) return null;

  const dragged = segments[at];
  let lastAt = at;
  let stop: RippleStop = 'end-of-task';
  let stopLabel: string | undefined;

  for (let j = at + 1; j < segments.length; j++) {
    // Contiguity is judged at ORIGINAL positions: the whole premise is that this
    // run was mis-placed as a unit, and a gap that only appears after the drag
    // says nothing about that.
    const gap = segments[j].startSec - segments[j - 1].endSec;
    if (gap >= RIPPLE_GAP_SEC) {
      stop = 'gap';
      stopLabel = segments[j].heard;
      break;
    }
    if (isAnchor(segments[j])) {
      stop = 'anchor';
      stopLabel = segments[j].heard;
      break;
    }
    lastAt = j;
  }

  const groupStart = dragged.startSec;
  const groupEnd = segments[lastAt].endSec;
  // Rows before the dragged one never move, so the one immediately before it is
  // a hard floor; the row the ripple stopped at is a hard ceiling.
  //
  // The `min`/`max` against the group's own edge is not belt-and-braces: a first
  // pass can and does emit rows that already overlap (on the user's sample,
  // "انا" ends at 3.620 and "اسمي" starts at 3.477). Taking the wall literally
  // would make such a row un-draggable in one direction for a reason that has
  // nothing to do with this edit. The rule that survives bad input is "a drag
  // may never make an overlap worse than it already is".
  const wallLeft = Math.min(at > 0 ? segments[at - 1].endSec : 0, groupStart);
  const wallRight = Math.max(
    lastAt + 1 < segments.length ? segments[lastAt + 1].startSec : fileDurationSec,
    groupEnd,
  );

  const maxDelta = Math.max(
    0,
    Math.min(CORRIDOR_SEC, wallRight - groupEnd, Math.max(0, fileDurationSec - groupEnd)),
  );
  const minDelta = Math.min(0, Math.max(-CORRIDOR_SEC, wallLeft - groupStart, -groupStart));

  return {
    segmentId,
    at,
    lastAt,
    followers: segments.slice(at + 1, lastAt + 1),
    stop,
    stopLabel,
    minDelta,
    maxDelta,
    originStart: groupStart,
    originGroupEnd: groupEnd,
  };
}

/**
 * Hold a raw delta inside the corridor and the walls.
 *
 * This file used to carry its own `clamp` with an extra `if (hi < lo) return lo`
 * guard, which `lib/num.clamp` does not have. Dropping it is safe HERE and only
 * here: `maxDelta` is built as `Math.max(0, …)` and `minDelta` as
 * `Math.min(0, …)`, so `minDelta <= 0 <= maxDelta` by construction and the
 * inverted-range branch was unreachable. `WaveTrimmer.tsx` keeps the guarded
 * copy on purpose — see the note there.
 */
export function clampDelta(plan: MovePlan, delta: number): number {
  return clamp(delta, plan.minDelta, plan.maxDelta);
}

export interface SnapResult {
  /** The delta to use — already clamped. */
  delta: number;
  /** The target the START landed on, in file seconds, or null. */
  snappedTo: number | null;
}

/**
 * Magnetically pull the dragged START onto the nearest acoustic landmark.
 *
 * Only the start snaps: the duration is fixed, so snapping the end as well would
 * fight the start for the same single degree of freedom. `targets` are onset and
 * speech-rise times in FILE seconds, ascending is not required.
 */
export function snapDelta(plan: MovePlan, rawDelta: number, targets: ArrayLike<number>): SnapResult {
  const delta = clampDelta(plan, rawDelta);
  const want = plan.originStart + delta;

  let best: number | null = null;
  let bestDist = SNAP_CAPTURE_SEC;
  for (let i = 0; i < targets.length; i++) {
    const t = targets[i];
    const d = Math.abs(t - want);
    if (d < bestDist) {
      bestDist = d;
      best = t;
    }
  }
  if (best === null) return { delta, snappedTo: null };

  const snapped = clampDelta(plan, best - plan.originStart);
  // The clamp may have pushed us back off the target (the wall wins over the
  // magnet); only report a snap that actually landed.
  if (Math.abs(plan.originStart + snapped - best) > 1e-6) return { delta, snappedTo: null };
  return { delta: snapped, snappedTo: best };
}

export interface MovedRow {
  segment: Segment;
  startSec: number;
  endSec: number;
}

/**
 * The rows a plan moves, at their new positions. The dragged row comes first.
 *
 * Durations are preserved exactly (both edges take the same rounded delta), so a
 * move can never quietly resize a clip.
 */
export function applyPlan(segments: Segment[], plan: MovePlan, delta: number): MovedRow[] {
  const d = round3(clampDelta(plan, delta));
  const out: MovedRow[] = [];
  for (let i = plan.at; i <= plan.lastAt && i < segments.length; i++) {
    const s = segments[i];
    out.push({ segment: s, startSec: round3(s.startSec + d), endSec: round3(s.endSec + d) });
  }
  return out;
}

/**
 * Last check before the write: did this move break anything?
 *
 * Only the two SEAMS matter. Inside the moving run every row took the identical
 * delta, so the spacing it had is the spacing it keeps; everything outside the
 * run did not move at all. That leaves the joint before the first moved row and
 * the joint after the last one.
 *
 * Each seam is judged as a DELTA of overlap, not as an absolute: the task may
 * already contain overlapping rows from the first pass, and refusing to move a
 * clip because of a defect that was there before the user touched it would make
 * exactly the audio that most needs re-aligning the audio you cannot re-align.
 * A move must not make a seam worse; it is under no obligation to fix it.
 */
export function validateMoved(segments: Segment[], moved: MovedRow[]): string | null {
  if (moved.length === 0) return null;
  for (const m of moved) {
    if (!(m.endSec > m.startSec)) return `“${m.segment.heard}” would end before it starts.`;
    if (m.startSec < -1e-6) return `“${m.segment.heard}” would start before the recording does.`;
  }

  const first = moved[0];
  const last = moved[moved.length - 1];
  const firstAt = segments.findIndex((s) => s.id === first.segment.id);
  const lastAt = segments.findIndex((s) => s.id === last.segment.id);
  if (firstAt < 0 || lastAt < 0) return 'Those clips are no longer part of this task.';

  const before = segments[firstAt - 1];
  if (before) {
    const was = segments[firstAt].startSec - before.endSec;
    const now = first.startSec - before.endSec;
    if (now < Math.min(0, was) - 1e-6) return `“${first.segment.heard}” would overlap “${before.heard}”.`;
  }

  const after = segments[lastAt + 1];
  if (after) {
    const was = after.startSec - segments[lastAt].endSec;
    const now = after.startSec - last.endSec;
    if (now < Math.min(0, was) - 1e-6) return `“${last.segment.heard}” would overlap “${after.heard}”.`;
  }
  return null;
}

/** Human-facing sentence for the ripple preview. */
export function describeRipple(count: number, delta: number): string {
  const dir = delta >= 0 ? 'later' : 'earlier';
  const amount = `${Math.abs(delta).toFixed(2)}s`;
  if (count === 0) return `Move this clip ${amount} ${dir}.`;
  return `${count} following clip${count === 1 ? '' : 's'} will shift ${amount} ${dir}.`;
}
